import { useEffect, useMemo, useRef, useState } from 'react';
import { Clock, Users } from 'lucide-react';
import { textIn, type Answers } from '@rx-manager/forms';
import type { DraftAnswers } from '@rx-manager/forms/react';
import {
  zonedToday,
  type PublicBookingForm,
  type PublicBookingPage,
  type PublicBookingResult,
  type PublicFormDue,
  type PublicLocation,
  type PublicPartyPerson,
  type PublicService,
} from '@rx-manager/shared';
import { bookingApi, BookingApiError } from '../../../lib/api';
import { intlLocale, useI18n, type MessageKey } from '../../../lib/i18n';
import { dobProblem, isEmail, isUsablePhone, type FieldErrors } from '../../../lib/validate';
import { Alert, Button, Card, Field, HEADING, Input, StepActions, cn } from '../../../components/ui';
import { StepBar } from '../../../components/StepBar';
import { AppointmentHead } from '../../../components/AppointmentCard';
import { ConsentBox, Confirmation, TimeStep } from '../../BookingFlow';
import { QuestionsStep } from '../QuestionsStep';
import {
  NO_FORMS,
  confirmForms,
  firstUnsettled,
  formLang,
  formsReducer,
  hasForms,
  type BookingFormsState,
  type FormsAction,
} from '../booking-forms';
import { TextRemindersBox, hashOf, useHoldCountdown } from '../shared';

/** See `BookingFlow`: a tagged pair, because `MessageKey | string` is `string`. */
type Failure = { key: MessageKey } | { text: string };

type PartyStep =
  | { kind: 'time' }
  | { kind: 'person'; index: number }
  | { kind: 'questions'; index: number }
  | { kind: 'contact' }
  | { kind: 'review' };

interface Person {
  firstName: string;
  lastName: string;
  dob: string;
  healthCard: string;
  consent: boolean;
}

const emptyPerson = (): Person => ({ firstName: '', lastName: '', dob: '', healthCard: '', consent: false });

/**
 * Each person's own copy of the service's forms, answered on their own (CGS-D11). With no list
 * yet (still loading, or failed), a copy waits for it: see `afterDetails`.
 */
function formsFor(serviceId: string, list: PublicBookingForm[] | null): BookingFormsState {
  if (list === null) return { ...NO_FORMS, serviceId };
  return formsReducer(formsReducer(NO_FORMS, { type: 'load_start', serviceId }), {
    type: 'loaded',
    serviceId,
    forms: list,
  });
}

/**
 * Track CGS (CGS-D9 to D14) — one booking for a party of two to five, seen back to back by one
 * pharmacist: a time where the whole block fits; then each person's details and questions,
 * "Person n of m"; then the hold (CGS-D13), the contact once (CGS-D12), the review and one
 * confirmation. If the time went while they typed, they pick another and keep everything.
 *
 * Entered from the People step; leaving the time step goes back to it.
 */
export function PartyFlow({
  page,
  service,
  location,
  size,
  before,
  conditionId,
  forms: formsList,
  formsReady,
  initialDate,
  window: dates,
  onBack,
}: {
  page: PublicBookingPage;
  service: PublicService;
  location: PublicLocation;
  size: number;
  /** The booking flow's step names up to and including "People", for the step bar. */
  before: string[];
  conditionId?: string;
  /** The service's forms, once the booking flow has them; null while loading or after a failure. */
  forms: PublicBookingForm[] | null;
  /** The service's forms when they are in: the load under way, else a new one; null on failure. */
  formsReady: () => Promise<PublicBookingForm[] | null>;
  initialDate: string;
  /** Track CGS — a clinic's first and last dates. */
  window?: { from: string; to: string };
  onBack: () => void;
}) {
  const { t, locale } = useI18n();
  const slug = page.profile.slug;
  const [step, setStep] = useState<PartyStep>({ kind: 'time' });
  const [date, setDate] = useState(initialDate);
  const [slotStart, setSlotStart] = useState<string | null>(null);
  const [people, setPeople] = useState<Person[]>(() => Array.from({ length: size }, emptyPerson));
  const [forms, setForms] = useState<BookingFormsState[]>(() =>
    Array.from({ length: size }, () => formsFor(service.id, formsList)),
  );
  const [errors, setErrors] = useState<FieldErrors>({});
  const [contact, setContact] = useState({ email: '', phone: '' });
  const [texts, setTexts] = useState(false);
  const [hold, setHold] = useState<{ token: string; expiresAt: string } | null>(null);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [busy, setBusy] = useState(false);
  const [booked, setBooked] = useState<PublicBookingResult | null>(null);
  const holding = useRef(false);
  const attemptKey = useRef(cryptoId());
  const headingRef = useRef<HTMLHeadingElement>(null);
  const textOffer = page.textReminders ?? null;

  useEffect(() => {
    headingRef.current?.focus();
  }, [step]);

  // Give the time back if they leave the flow with one held.
  const holdRef = useRef(hold);
  holdRef.current = hold;
  useEffect(
    () => () => {
      const held = holdRef.current;
      if (held) void bookingApi.releaseHold(slug, held.token).catch(() => undefined);
    },
    [slug],
  );

  const releaseHold = async (): Promise<void> => {
    if (!hold) return;
    const token = hold.token;
    setHold(null);
    await bookingApi.releaseHold(slug, token).catch(() => undefined);
  };

  const dispatch = (index: number, action: FormsAction): void =>
    setForms((all) => all.map((state, i) => (i === index ? formsReducer(state, action) : state)));

  const setPerson = (index: number, patch: Partial<Person>): void =>
    setPeople((all) => all.map((person, i) => (i === index ? { ...person, ...patch } : person)));

  // ── Moving between steps ────────────────────────────────────────────────

  /**
   * After a person's details: their questions, if any are left, else the next person. The party
   * can open before the service's forms are in; their questions wait for them, as the single
   * flow's do, since a required form left out is only refused at Book.
   */
  const afterDetails = async (index: number): Promise<void> => {
    let state = forms[index]!;
    if (state.load !== 'ready') {
      setFailure(null);
      let list = formsList;
      if (list === null) {
        setBusy(true);
        list = await formsReady();
        setBusy(false);
      }
      if (list === null) {
        setFailure({ key: 'questions.loadFailed' });
        return;
      }
      const ready = list;
      state = formsFor(service.id, ready);
      setForms((all) => all.map((entry) => (entry.load === 'ready' ? entry : formsFor(service.id, ready))));
    }
    const next = hasForms(state) ? firstUnsettled(state) : -1;
    if (next >= 0) {
      dispatch(index, { type: 'open', index: next, returnTo: null });
      setStep({ kind: 'questions', index });
      return;
    }
    void afterPerson(index);
  };

  /** After a person is done: the next person, or the hold and then the contact. */
  const afterPerson = async (index: number): Promise<void> => {
    if (index + 1 < size) {
      setErrors({});
      setStep({ kind: 'person', index: index + 1 });
      return;
    }
    await takeHold();
  };

  /** CGS-D13 — the party's hold, after everyone's details and questions. */
  const takeHold = async (): Promise<void> => {
    if (hold) {
      setStep({ kind: 'contact' });
      return;
    }
    if (!slotStart || holding.current) return;
    holding.current = true;
    setBusy(true);
    setFailure(null);
    try {
      const held = await bookingApi.hold(slug, {
        serviceId: service.id,
        locationId: location.id,
        startAt: slotStart,
        partySize: size,
      });
      setHold({ token: held.holdToken, expiresAt: held.expiresAt });
      setStep({ kind: 'contact' });
    } catch (error) {
      setFailure(messageOf(error));
      if (error instanceof BookingApiError && (error.isSlotGone || error.isMissing)) {
        // Pick another time; everyone's details and answers stay.
        setSlotStart(null);
        setStep({ kind: 'time' });
        setFailure({ key: 'party.timeGone' });
      }
    } finally {
      holding.current = false;
      setBusy(false);
    }
  };

  const goBack = (): void => {
    setFailure(null);
    setErrors({});
    switch (step.kind) {
      case 'time':
        onBack();
        return;
      case 'person':
        if (step.index === 0) {
          void releaseHold();
          setStep({ kind: 'time' });
          return;
        }
        backToPerson(step.index - 1);
        return;
      case 'questions': {
        const state = forms[step.index]!;
        if (state.cursor > 0) {
          dispatch(step.index, { type: 'open', index: state.cursor - 1, returnTo: null });
          return;
        }
        setStep({ kind: 'person', index: step.index });
        return;
      }
      case 'contact':
        backToPerson(size - 1);
        return;
      case 'review':
        setStep({ kind: 'contact' });
        return;
    }
  };

  /** Back into a person: their last form, else their details. */
  const backToPerson = (index: number): void => {
    const state = forms[index]!;
    if (hasForms(state)) {
      dispatch(index, { type: 'open', index: state.entries.length - 1, returnTo: null });
      setStep({ kind: 'questions', index });
      return;
    }
    setStep({ kind: 'person', index });
  };

  // ── Checks ──────────────────────────────────────────────────────────────

  const checkPerson = (index: number): boolean => {
    const person = people[index]!;
    const found: FieldErrors = {};
    if (!person.firstName.trim()) found.firstName = 'error.fieldRequired';
    if (!person.lastName.trim()) found.lastName = 'error.fieldRequired';
    const dob = dobProblem(person.dob);
    if (dob) found.dob = dob;
    if (service.consentText && !person.consent) found.consent = 'error.consentRequired';
    // The same person twice is refused by the server too (CGS-D11).
    const twice = people.some(
      (other, i) =>
        i < index &&
        other.firstName.trim().toLowerCase() === person.firstName.trim().toLowerCase() &&
        other.lastName.trim().toLowerCase() === person.lastName.trim().toLowerCase() &&
        other.dob === person.dob,
    );
    if (twice) found.firstName = 'party.samePerson';
    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const checkContact = (): boolean => {
    const found: FieldErrors = {};
    if (!contact.email.trim()) found.email = 'error.fieldRequired';
    else if (!isEmail(contact.email)) found.email = 'error.emailInvalid';
    if (!contact.phone.trim()) found.phone = 'error.fieldRequired';
    else if (!isUsablePhone(contact.phone)) found.phone = 'error.phoneInvalid';
    setErrors(found);
    return Object.keys(found).length === 0;
  };

  // ── Confirm ─────────────────────────────────────────────────────────────

  const submit = async (): Promise<void> => {
    if (!hold) return;
    setBusy(true);
    setFailure(null);
    try {
      const body: PublicPartyPerson[] = people.map((person, index) => {
        const answers = confirmForms(forms[index]!, locale);
        return {
          firstName: person.firstName.trim(),
          lastName: person.lastName.trim(),
          dob: person.dob,
          ...(page.collectsHealthCard && person.healthCard.trim()
            ? { healthCard: person.healthCard.trim() }
            : {}),
          consentGiven: service.consentText ? person.consent : false,
          ...(service.consentText ? { consentTextVersion: hashOf(service.consentText) } : {}),
          ...(answers ? { forms: answers } : {}),
        };
      });
      const textsChosen = texts && Boolean(contact.phone.trim());
      const result = await bookingApi.confirmParty(slug, {
        holdToken: hold.token,
        contact: { email: contact.email.trim(), phone: contact.phone.trim(), province: location.province },
        people: body,
        idempotencyKey: attemptKey.current,
        ...(textOffer ? { textReminders: textsChosen, language: locale } : {}),
        ...(conditionId ? { conditionId } : {}),
      });
      setHold(null);
      setBooked(result);
    } catch (error) {
      attemptKey.current = cryptoId();
      const person =
        error instanceof BookingApiError && typeof error.extra.person === 'number' ? error.extra.person : null;
      if (person !== null && person >= 1 && person <= size) {
        // One person's refusal: back to them, the hold kept (CGS-D13).
        setFailure({ text: t('party.aboutPerson', { n: person, message: error instanceof Error ? error.message : '' }) });
        setStep({ kind: 'person', index: person - 1 });
        return;
      }
      setFailure(messageOf(error));
      if (error instanceof BookingApiError && (error.isSlotGone || error.isMissing)) {
        setHold(null);
        setSlotStart(null);
        setStep({ kind: 'time' });
        setFailure({ key: 'party.timeGone' });
      }
    } finally {
      setBusy(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────

  const labels = [
    ...before,
    t('step.time'),
    ...people.map((_, i) => t('party.personStep', { n: i + 1 })),
    t('party.contactStep'),
    t('step.review'),
  ];
  const current =
    before.length +
    (step.kind === 'time'
      ? 0
      : step.kind === 'person' || step.kind === 'questions'
        ? 1 + step.index
        : step.kind === 'contact'
          ? 1 + size
          : 2 + size);

  if (booked) return <PartyConfirmation booking={booked} context={{ slug, serviceId: service.id, locationId: location.id }} />;

  const index = step.kind === 'person' || step.kind === 'questions' ? step.index : -1;
  const person = index >= 0 ? people[index]! : null;
  const formState = step.kind === 'questions' ? forms[step.index]! : null;
  const formEntry = formState ? formState.entries[formState.cursor] : undefined;

  return (
    <div className="space-y-6">
      <StepBar labels={labels} current={current} />

      <h1 ref={headingRef} tabIndex={-1} className={HEADING}>
        {step.kind === 'time'
          ? t('party.timeHeading', { service: service.title, count: size })
          : step.kind === 'person'
            ? t('party.personHeading', { n: step.index + 1, total: size })
            : step.kind === 'questions' && formEntry
              ? textIn(formEntry.form.schema.title, formLang(formEntry.form, locale))
              : step.kind === 'contact'
                ? t('party.contactHeading')
                : t('review.heading')}
      </h1>
      {step.kind === 'questions' && person && (
        <p className="text-base text-muted-foreground">
          {t('party.questionsFor', { name: person.firstName, n: index + 1, total: size })}
        </p>
      )}

      {failure && <Alert>{'key' in failure ? t(failure.key) : failure.text}</Alert>}

      {step.kind === 'time' && (
        <TimeStep
          slug={slug}
          service={service}
          location={location}
          date={date}
          onDate={setDate}
          selected={slotStart}
          onSelect={setSlotStart}
          onContinue={() => {
            if (!slotStart) return;
            setFailure(null);
            setErrors({});
            // Back from a changed time: the hold for the old one is given back.
            if (hold) void releaseHold();
            setStep({ kind: 'person', index: 0 });
          }}
          onBack={goBack}
          busy={busy}
          phone={location.phone ?? page.profile.supportPhone}
          partySize={size}
          dates={dates}
        />
      )}

      {step.kind === 'person' && person && (
        <PersonStep
          index={step.index}
          person={person}
          onChange={(patch) => setPerson(step.index, patch)}
          errors={errors}
          collectsHealthCard={page.collectsHealthCard}
          consentText={service.consentText}
          onContinue={() => {
            if (checkPerson(step.index)) void afterDetails(step.index);
          }}
          onBack={goBack}
          busy={busy}
        />
      )}

      {step.kind === 'questions' && formState && formEntry && (
        <QuestionsStep
          state={formState}
          pageLang={locale}
          today={zonedToday(location.timezone)}
          phone={location.phone ?? page.profile.supportPhone ?? null}
          busy={busy}
          onAnswers={(answers: DraftAnswers) => dispatch(step.index, { type: 'answers', answers })}
          onFinish={async (checked: Answers) => {
            dispatch(step.index, { type: 'finish', checked });
            const after = formsReducer(formState, { type: 'finish', checked });
            const next = firstUnsettled(after);
            if (next >= 0) {
              dispatch(step.index, { type: 'open', index: next, returnTo: null });
              return;
            }
            await afterPerson(step.index);
          }}
          onSkip={() => {
            dispatch(step.index, { type: 'skip' });
            const after = formsReducer(formState, { type: 'skip' });
            const next = firstUnsettled(after);
            if (next >= 0) dispatch(step.index, { type: 'open', index: next, returnTo: null });
            else void afterPerson(step.index);
          }}
          onAnswerNow={() =>
            dispatch(step.index, { type: 'open', index: formState.cursor, view: { kind: 'runner', mount: 0 } })
          }
          onBack={goBack}
          onLeave={onBack}
        />
      )}

      {step.kind === 'contact' && (
        <ContactStep
          contact={contact}
          onChange={setContact}
          errors={errors}
          textOffer={textOffer}
          texts={texts}
          onTexts={setTexts}
          hold={hold}
          onContinue={() => {
            if (checkContact()) setStep({ kind: 'review' });
          }}
          onBack={goBack}
        />
      )}

      {step.kind === 'review' && slotStart && (
        <PartyReview
          service={service}
          location={location}
          startAt={slotStart}
          people={people}
          contact={contact}
          texts={textOffer ? texts && Boolean(contact.phone.trim()) : null}
          hold={hold}
          answered={forms.map((state) => (hasForms(state) ? state.entries.filter((e) => e.status === 'done').length : 0))}
          onConfirm={() => void submit()}
          onChange={goBack}
          busy={busy}
        />
      )}
    </div>
  );
}

// ── Steps ────────────────────────────────────────────────────────────────────

function PersonStep({
  index,
  person,
  onChange,
  errors,
  collectsHealthCard,
  consentText,
  onContinue,
  onBack,
  busy,
}: {
  index: number;
  person: Person;
  onChange: (patch: Partial<Person>) => void;
  errors: FieldErrors;
  collectsHealthCard: boolean;
  consentText: string | null;
  onContinue: () => void;
  onBack: () => void;
  busy: boolean;
}) {
  const { t } = useI18n();
  const id = (name: string) => `person-${index + 1}-${name}`;
  const error = (key: string) => {
    const found = errors[key];
    return found ? t(found) : undefined;
  };
  return (
    <form
      noValidate
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        onContinue();
      }}
    >
      <p className="text-base text-muted-foreground">{index === 0 ? t('party.firstPerson') : t('party.nextPerson')}</p>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id={id('first')} label={t('details.firstName')} error={error('firstName')} required requiredLabel={t('details.required')}>
          {(props) => (
            <Input {...props} autoComplete="off" value={person.firstName} onChange={(e) => onChange({ firstName: e.target.value })} />
          )}
        </Field>
        <Field id={id('last')} label={t('details.lastName')} error={error('lastName')} required requiredLabel={t('details.required')}>
          {(props) => (
            <Input {...props} autoComplete="off" value={person.lastName} onChange={(e) => onChange({ lastName: e.target.value })} />
          )}
        </Field>
      </div>
      <Field
        id={id('dob')}
        label={t('details.dob')}
        help={t('details.dobHelp')}
        error={error('dob')}
        required
        requiredLabel={t('details.required')}
      >
        {(props) => (
          <Input {...props} type="date" value={person.dob} onChange={(e) => onChange({ dob: e.target.value })} />
        )}
      </Field>
      {collectsHealthCard && (
        <Field id={id('card')} label={t('details.healthCard')} help={t('details.healthCardHelp')}>
          {(props) => (
            <Input {...props} autoComplete="off" value={person.healthCard} onChange={(e) => onChange({ healthCard: e.target.value })} />
          )}
        </Field>
      )}
      {consentText && (
        <section aria-labelledby={id('consent-heading')} className="space-y-3">
          <h2 id={id('consent-heading')} className="text-base font-semibold text-heading">
            {t('consent.heading')}
          </h2>
          <Card tone="muted" className="whitespace-pre-line text-base">
            {consentText}
          </Card>
          <ConsentBox
            checked={person.consent}
            onChange={(value) => onChange({ consent: value })}
            error={error('consent')}
          />
        </section>
      )}
      <StepActions>
        <Button type="button" variant="outline" onClick={onBack}>
          {t('step.back')}
        </Button>
        <Button type="submit" loading={busy}>
          {t('step.continue')}
        </Button>
      </StepActions>
    </form>
  );
}

function ContactStep({
  contact,
  onChange,
  errors,
  textOffer,
  texts,
  onTexts,
  hold,
  onContinue,
  onBack,
}: {
  contact: { email: string; phone: string };
  onChange: (next: { email: string; phone: string }) => void;
  errors: FieldErrors;
  textOffer: PublicBookingPage['textReminders'] | null;
  texts: boolean;
  onTexts: (value: boolean) => void;
  hold: { expiresAt: string } | null;
  onContinue: () => void;
  onBack: () => void;
}) {
  const { t } = useI18n();
  const error = (key: string) => {
    const found = errors[key];
    return found ? t(found) : undefined;
  };
  return (
    <form
      noValidate
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        onContinue();
      }}
    >
      <HoldNotice hold={hold} />
      <p className="text-base text-muted-foreground">{t('party.contactHelp')}</p>
      <Field id="party-email" label={t('details.email')} error={error('email')} required requiredLabel={t('details.required')}>
        {(props) => (
          <Input
            {...props}
            type="email"
            autoComplete="email"
            value={contact.email}
            onChange={(e) => onChange({ ...contact, email: e.target.value })}
          />
        )}
      </Field>
      <Field id="party-phone" label={t('details.phone')} error={error('phone')} required requiredLabel={t('details.required')}>
        {(props) => (
          <Input
            {...props}
            type="tel"
            autoComplete="tel"
            value={contact.phone}
            onChange={(e) => onChange({ ...contact, phone: e.target.value })}
          />
        )}
      </Field>
      {textOffer && <TextRemindersBox wordings={textOffer.wordings} checked={texts} onChange={onTexts} />}
      <StepActions>
        <Button type="button" variant="outline" onClick={onBack}>
          {t('step.back')}
        </Button>
        <Button type="submit">{t('step.continue')}</Button>
      </StepActions>
    </form>
  );
}

function PartyReview({
  service,
  location,
  startAt,
  people,
  contact,
  texts,
  hold,
  answered,
  onConfirm,
  onChange,
  busy,
}: {
  service: PublicService;
  location: PublicLocation;
  startAt: string;
  people: Person[];
  contact: { email: string; phone: string };
  texts: boolean | null;
  hold: { expiresAt: string } | null;
  /** Forms answered, per person. */
  answered: number[];
  onConfirm: () => void;
  onChange: () => void;
  busy: boolean;
}) {
  const { t, locale } = useI18n();
  const when = useMemo(
    () =>
      new Intl.DateTimeFormat(intlLocale(locale), {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: location.timezone,
      }).format(new Date(startAt)),
    [startAt, locale, location.timezone],
  );
  return (
    <div className="space-y-6">
      <HoldNotice hold={hold} />
      <div className="overflow-hidden rounded-xl border border-border">
        <AppointmentHead
          iso={startAt}
          timeZone={location.timezone}
          first={{ label: t('done.when'), value: when }}
          second={{ label: t('done.what'), value: service.title }}
        />
        <dl className="divide-y divide-border px-4 sm:px-5">
          <ReviewRow label={t('done.where')} value={[location.name, location.address].filter(Boolean).join(' · ')} />
          <ReviewRow label={t('review.who')} value={people.map((p) => `${p.firstName} ${p.lastName}`).join(', ')} />
          <ReviewRow label={t('details.email')} value={contact.email} />
          <ReviewRow label={t('details.phone')} value={contact.phone} />
          {texts !== null && (
            <ReviewRow
              label={t('review.texts')}
              value={texts ? t('review.textsYes', { phone: contact.phone }) : t('review.textsNo')}
            />
          )}
        </dl>
      </div>
      {answered.some((n) => n > 0) && (
        <ul className="space-y-1 text-sm text-muted-foreground">
          {people.map((person, i) =>
            answered[i]! > 0 ? (
              <li key={i}>{t('party.answersGiven', { name: person.firstName, count: answered[i]! })}</li>
            ) : null,
          )}
        </ul>
      )}
      <p className="text-sm text-muted-foreground">{t('party.oneEmail')}</p>
      <StepActions>
        <Button variant="outline" onClick={onChange}>
          {t('review.change')}
        </Button>
        <Button loading={busy} onClick={onConfirm}>
          <Users className="h-4 w-4" aria-hidden="true" />
          {t('party.confirm', { count: people.length })}
        </Button>
      </StepActions>
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap justify-between gap-x-4 gap-y-1 py-3">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-base font-medium text-heading">{value}</dd>
    </div>
  );
}

/** The done screen: the anchor's booking, then everyone's forms, as one list (CGS-D12). */
function PartyConfirmation({
  booking,
  context,
}: {
  booking: PublicBookingResult;
  context: { slug: string; serviceId: string; locationId: string };
}) {
  const { t, locale } = useI18n();
  const party = booking.party;
  // One list, each form named for its person: one "Before your visit" on the page.
  const forms: PublicFormDue[] = (party?.people ?? []).flatMap((person) =>
    (person.forms ?? []).map((form) => ({
      ...form,
      formId: `${person.position}:${form.formId}`,
      title: {
        en: `${person.firstName} — ${form.title.en}`,
        ...(form.title.fr ? { fr: `${person.firstName} — ${form.title.fr}` } : {}),
      },
    })),
  );
  return (
    <div className="space-y-6">
      <Confirmation booking={{ ...booking, forms }} locale={locale} context={context} />
      {party && (
        <section aria-labelledby="party-people" className="space-y-2">
          <h2 id="party-people" className="text-lg font-semibold text-heading">
            {t('party.bookedFor', { count: party.people.length })}
          </h2>
          <ul className="space-y-1 text-base text-foreground">
            {party.people.map((person) => (
              <li key={person.position}>
                {person.firstName} {person.lastName}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/** The hold's last minute, as the single booking shows it. */
function HoldNotice({ hold }: { hold: { expiresAt: string } | null }) {
  const { t } = useI18n();
  const msLeft = useHoldCountdown(hold?.expiresAt ?? null);
  if (msLeft === null || msLeft > 60_000) return null;
  return (
    <p
      aria-live="polite"
      className={cn(
        'flex items-center gap-2.5 rounded-xl border px-4 py-3 text-base font-medium text-heading',
        msLeft > 0 ? 'border-warning/50 bg-warning/15' : 'border-danger/30 bg-danger/5',
      )}
    >
      <Clock className={cn('h-5 w-5 shrink-0', msLeft > 0 ? 'text-warning-foreground' : 'text-danger')} aria-hidden="true" />
      {msLeft > 0 ? t('details.holdTimer') : t('details.holdExpired')}
    </p>
  );
}

function messageOf(error: unknown): Failure {
  if (!(error instanceof BookingApiError)) return { key: 'error.unknown' };
  if (error.message === 'offline') return { key: 'error.offline' };
  if (error.isSlotGone) return { key: 'error.slotGone' };
  if (error.message === 'unknown') return { key: 'error.unknown' };
  return { text: error.message };
}

/** Random enough to make an idempotency key unique per attempt. */
function cryptoId(): string {
  return `pub-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}
